import {HttpClientService} from "services/httpClient/httpClientService";
import config from "config/config";
import {AccountService} from "../../account/accountService";
import {ApiResponse} from "../apiResponse";
import {CrudLaegekursusInputModel} from "../../../pages/kurserPage/components/crudLaegekursusInputModel";
import {LogbogLaegekursusCrudModel} from "./logbogLaegekursusCrudModel";
import {GodkendPostModel} from "../forloeb/godkendPostModel";
import {LaegekursusGruppeModel} from "./laegekursusGruppeModel";
import {LaegekursusGruppeResponseModel} from "./laegekursusGruppeResponseModel";
import {GetLaegekursusGruppeTranslation} from "../../../core/sharedmodels/kursus/laegekursusGruppeTypeEnum";
import {LaegekursusModel} from "./laegekursusModel";
import {LaegekursusResponseModel} from "./laegekursusResponseModel";
import {RemoveGodkendelsePostModel} from "./removeGodkendelsePostModel";
import {udeladKursusRequestModel} from "./udeladKursusRequestModel";
import {
    KursusoverviewFilterAsyncListInput,
    KursusoverviewModel
} from "../../../pages/kursusOverview/models/KursusoverviewModel";
import {PromiseWithCancel} from "../../httpClient/promiseWithCancel";
import {AsyncResponseModel} from "../asyncResponseModel";
import {FilterModel} from "../../../core/sharedmodels/filterModel";
import { KursusoverviewBrugerKursusRaekkeResponseModel } from "./kursusOverviewBrugerKursusRaekkeResponseModel";
import { UserSendEmailResponseModel } from "../stamdataUser/UserSendEmailResponseModel";

export class LogbogLaegekursusApi {
    private httpClientService: HttpClientService;
    private userService: AccountService;

    private baseUrl = () => config.logbogApiUrl + "api/laegekursus/";
    private laegekursusGodkendUrl = () => this.baseUrl() + "godkend/";
    private laegekursusGodkendMultipleUrl = () => this.baseUrl() + "godkendMultiple/";
    private laegekursusFjernGodkendelseUrl = () => this.baseUrl() + "removeGodkendelse/";
    private getDeteletedLaegekurserForUserUrl = () => this.baseUrl() + "deletedLaegekurserByUser/";
    private getLaegekursusGrupperByIdUrl = () => this.baseUrl() + "byGruppeId/";
    private editLaegekursusUrl = () => this.baseUrl() + "editLaegekursus";
    private gendanLaegekursusUrl = () => this.baseUrl() + "gendanLaegekursus/";
    private laegekursusRemoveKlarmeldingUrl = () => this.baseUrl() + "removeKlarmelding/";
    public getKlarmeldteKurserUrl = () => this.baseUrl() + "getKlarmeldteKurser/";
    public getKursusoverviewUrl = () => this.baseUrl() + "kursusoverview/";
    public getKlarmeldteKurserTableUrl = () => this.baseUrl() + "getKlarmeldteKurserTable/";
    public udeladKursusFraKlarmeldingerUrl = () => this.baseUrl() + "udeladKursusFraKlarmeldinger/";
    public getKursusoverviewUserEmailsUrl = () => this.baseUrl() + "kursusoverviewUserEmails/"

    private getKursusoverviewWithCancel: PromiseWithCancel<ApiResponse<AsyncResponseModel<KursusoverviewBrugerKursusRaekkeResponseModel>>>;

    cancelPendingRequests(): void {
        if(this.getKursusoverviewWithCancel) {
            this.getKursusoverviewWithCancel.cancel();
        }
    }

    constructor(httpClientService: HttpClientService, userService: AccountService) {
        this.httpClientService = httpClientService;
        this.userService = userService;
    }

    async getKursusGrupper(brugerId: string): Promise<LaegekursusGruppeModel[]> {
        const url = this.getLaegekursusGrupperByIdUrl() + brugerId;
        const result = await this.httpClientService.Get<ApiResponse<LaegekursusGruppeResponseModel[]>>(url);
        return result.data.map(kursus => {
            return new LaegekursusGruppeModel(kursus)
        }).sort((a, b) => GetLaegekursusGruppeTranslation(a.kursusGruppeType).localeCompare(GetLaegekursusGruppeTranslation(b.kursusGruppeType)));;
    }

    async getLaegeKursusGruppe(gruppeId: string): Promise<LaegekursusGruppeModel> {
        const url = this.getLaegekursusGrupperByIdUrl() + gruppeId;
        const result = await this.httpClientService.Get<ApiResponse<LaegekursusGruppeResponseModel>>(url);
        return new LaegekursusGruppeModel(result.data);
    }

    async createLaegekursus(inputModel: CrudLaegekursusInputModel): Promise<string> {
        const postModel = new LogbogLaegekursusCrudModel(inputModel);
        const url = this.baseUrl();
        const result = await this.httpClientService.Post<ApiResponse<string>, LogbogLaegekursusCrudModel>(url, postModel);
        return result.data;
    }

    async editLaegekursus(inputModel: CrudLaegekursusInputModel) {
        const patchModel = new LogbogLaegekursusCrudModel(inputModel);
        const url = this.editLaegekursusUrl();
        const result = await this.httpClientService.Post<ApiResponse<string>, LogbogLaegekursusCrudModel>(url, patchModel);
        return result.data;
    }

    async deleteLaegekursus(id: string): Promise<string> {
        const url = this.baseUrl() + id;
        const result = await this.httpClientService.Delete<ApiResponse<string>>(url);
        return result.data;
    }

    async godkendLaegekursus(contextId: string, uddannelsessted: string, godkendtPaaAndetGrundlag: boolean, brugerMaalbeskrivelseId: string, kommentar?: string): Promise<ApiResponse<string>> {
        const body = new GodkendPostModel(contextId, this.userService.getUser().UserId, this.userService.getUser().CurrentRolleTitel, uddannelsessted, kommentar, godkendtPaaAndetGrundlag, brugerMaalbeskrivelseId);
        const url = this.laegekursusGodkendUrl();
        return await this.httpClientService.Post<ApiResponse<string>, GodkendPostModel>(url, body, true);
    }

    async godkendLaegekurser(laegekursusIds: string[]): Promise<ApiResponse<boolean>> {
        const godkenderUserId = this.userService.getUser().UserId;
        const godkenderRoleTitel = this.userService.getUser().CurrentRolleTitel;
        const body = laegekursusIds.map(laegekursusId => new GodkendPostModel(laegekursusId, godkenderUserId, godkenderRoleTitel));
        const url = this.laegekursusGodkendMultipleUrl();
        return await this.httpClientService.Post<ApiResponse<boolean>, GodkendPostModel[]>(url, body, true);
    }

    async removeGodkendelseLaegekursus(kursusId: string, brugerMaalbeskrivelseId?: string): Promise<string> {
        const body = new RemoveGodkendelsePostModel(kursusId, brugerMaalbeskrivelseId);
        const url = this.laegekursusFjernGodkendelseUrl();
        const result = await this.httpClientService.Post<ApiResponse<string>, RemoveGodkendelsePostModel>(url, body);
        return result.data;
    }

    async getDeletedKurserForUser(userId: string, uddannelseType: string): Promise<LaegekursusModel[]> {
        const urlParamenters = `${userId}/${uddannelseType}`
        const url = this.getDeteletedLaegekurserForUserUrl() + urlParamenters;
        const result = await this.httpClientService.Get<ApiResponse<LaegekursusResponseModel[]>>(url);
        return result.data.map(result => {
            return new LaegekursusModel(result)
        }).sort((a, b) => GetLaegekursusGruppeTranslation(a.gruppeType).localeCompare(GetLaegekursusGruppeTranslation(b.gruppeType)));
    }

    async gendanLaegekursus(laegekursusId: string): Promise<string> {
        const url = this.gendanLaegekursusUrl();
        const result = await this.httpClientService.Post<ApiResponse<string>, string>(url, laegekursusId);
        return result.data;
    }

    async removeKlarmelding(laegekursusId: string) {
        const url = this.laegekursusRemoveKlarmeldingUrl();
        const result = await this.httpClientService.Post<ApiResponse<string>, string>(url, laegekursusId);
        return result.data;

    }

    async setUdeladKursusFraKlarmeldinger(laegekursusIds: string[], kommentar: string): Promise<ApiResponse<boolean>> {
        const requestModel: udeladKursusRequestModel = {laegekursusIds, kommentar};
        const url = this.udeladKursusFraKlarmeldingerUrl();
        return await this.httpClientService.Post<ApiResponse<boolean>, udeladKursusRequestModel>(url, requestModel);
    }


    async getKlarmeldteKurser(): Promise<LaegekursusModel[]> {
        const url = this.getKlarmeldteKurserUrl();
        const result = await this.httpClientService.Get<ApiResponse<LaegekursusResponseModel[]>>(url);
        return result.data.map(result => {
            return new LaegekursusModel(result)
        }).sort((a, b) => GetLaegekursusGruppeTranslation(a.gruppeType).localeCompare(GetLaegekursusGruppeTranslation(b.gruppeType)));
    }

    async getKursusoverview(kursusoverviewFilter: KursusoverviewFilterAsyncListInput): Promise<FilterModel<KursusoverviewModel>> {
        if (this.getKursusoverviewWithCancel)
            this.getKursusoverviewWithCancel.cancel();

        const url = this.getKursusoverviewUrl();
        this.getKursusoverviewWithCancel = this.httpClientService.PostWithCancellation<ApiResponse<AsyncResponseModel<KursusoverviewBrugerKursusRaekkeResponseModel>>, KursusoverviewFilterAsyncListInput>(url, kursusoverviewFilter);
        
        const result = await this.getKursusoverviewWithCancel;
        const kursusOverviewModels = result.data.data.map(KursusoverviewModel.FromResponseModel);
        return new FilterModel<KursusoverviewModel>(kursusOverviewModels, result.data.recordsFiltered, result.data.recordsTotal);
    }

    async getKursusOverviewUserEmails(kursusoverviewFilter: KursusoverviewFilterAsyncListInput): Promise<ApiResponse<UserSendEmailResponseModel[]>> {
        const url = this.getKursusoverviewUserEmailsUrl();
        const result = await this.httpClientService.Post<ApiResponse<UserSendEmailResponseModel[]>, KursusoverviewFilterAsyncListInput>(url, kursusoverviewFilter, true);
        return result
    }
}
